// =============================================================================
// SIREPPAR - Notas a Afiliados Duplicados (Art. 16° Res. 002/2026 I)
// SOLO USO INTERNO — no visible para usuarios de partido
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

type EstadoNota = 'PENDIENTE' | 'GENERADA' | 'DESCARGADA' | 'ENVIADA';

interface NotaAfiliado {
  id: string;
  dni: string;
  apellido: string;
  nombre: string;
  partidos: Array<string | { denominacion: string; tipo: string }>;
  estado: EstadoNota;
  fechaGeneracion: string | null;
  fechaDescarga: string | null;
  fechaEnvio: string | null;
}

interface Resumen {
  totalDuplicados: number;
  partidosInvolucrados: number;
  notasTotal: number;
  pendientes: number;
  generadas: number;
  descargadas: number;
  enviadas: number;
}

const ESTADO_CONFIG: Record<EstadoNota, { label: string; color: string; bg: string }> = {
  PENDIENTE:  { label: 'Pendiente',  color: 'text-amber-700', bg: 'bg-amber-100'  },
  GENERADA:   { label: 'Generada',   color: 'text-blue-700',  bg: 'bg-blue-100'   },
  DESCARGADA: { label: 'Descargada', color: 'text-green-700', bg: 'bg-green-100'  },
  ENVIADA:    { label: 'Enviada',    color: 'text-gray-700',  bg: 'bg-gray-200'   },
};

const ROLES_ESCRITURA = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'];
function formatDNI(dni: string) {
  return dni.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatFecha(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function NotasAfiliadosDuplicadosPage() {
  const { user } = useAuth();
  const puedeEscribir = user && ROLES_ESCRITURA.includes(user.rol);

  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [notas, setNotas] = useState<NotaAfiliado[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [estadoFiltro, setEstadoFiltro] = useState<string>('');

  const [loadingResumen, setLoadingResumen] = useState(false);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [descargandoTodas, setDescargandoTodas] = useState(false);
  const [descargandoId, setDescargandoId] = useState<string | null>(null);
  const [marcandoId, setMarcandoId] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  const loadResumen = useCallback(async () => {
    setLoadingResumen(true);
    try {
      const r = await api.get('/notas-afiliados/resumen', { params: { _t: Date.now() } });
      setResumen(r.data.data);
    } catch {
      toast.error('Error al cargar el resumen');
    } finally {
      setLoadingResumen(false);
    }
  }, []);

  const loadNotas = useCallback(async () => {
    setLoadingNotas(true);
    try {
      const params: Record<string, any> = { page: currentPage, pageSize: PAGE_SIZE };
      if (estadoFiltro) params.estado = estadoFiltro;
      const r = await api.get('/notas-afiliados/', { params });
      const payload = r.data.data ?? {};
    const data = payload.notas ?? [];
    setNotas(data);
    setTotal(payload.total ?? data.length);
    setTotalPages(Math.ceil((payload.total ?? data.length) / PAGE_SIZE) || 1);
    } catch {
      toast.error('Error al cargar las notas');
    } finally {
      setLoadingNotas(false);
    }
  }, [currentPage, estadoFiltro]);

  useEffect(() => { loadResumen(); }, [loadResumen]);
  useEffect(() => { loadNotas(); }, [loadNotas]);

  const handleGenerar = async () => {
    if (!confirm('¿Generar notas para todos los afiliados con estado DUPLICADO que aún no tienen nota?')) return;
    setGenerando(true);
    try {
      const r = await api.post('/notas-afiliados/generar-pendientes');
      toast.success(r.data.message || 'Notas generadas');
      loadResumen();
      loadNotas();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Error al generar notas');
    } finally {
      setGenerando(false);
    }
  };

  const descargarPdf = async (nota: NotaAfiliado) => {
    setDescargandoId(nota.id);
    try {
      const r = await api.get(`/notas-afiliados/${nota.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nota_${nota.dni}_${nota.apellido}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setNotas(prev => prev.map(n => {
        if (n.id !== nota.id) return n;
        const nuevoEstado: EstadoNota =
          n.estado === 'PENDIENTE' ? 'GENERADA' :
          n.estado === 'GENERADA' ? 'DESCARGADA' : n.estado;
        return { ...n, estado: nuevoEstado };
      }));
      loadResumen();
    } catch {
      toast.error('Error al descargar el PDF');
    } finally {
      setDescargandoId(null);
    }
  };

  const handleMarcarEnviada = async (nota: NotaAfiliado) => {
    if (!confirm(`¿Marcar la nota de ${nota.apellido}, ${nota.nombre} como enviada?`)) return;
    setMarcandoId(nota.id);
    try {
      await api.patch(`/notas-afiliados/${nota.id}/enviada`);
      toast.success('Nota marcada como enviada');
      setNotas(prev => prev.map(n =>
        n.id === nota.id ? { ...n, estado: 'ENVIADA', fechaEnvio: new Date().toISOString() } : n
      ));
      loadResumen();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Error al marcar como enviada');
    } finally {
      setMarcandoId(null);
    }
  };

  const handleDescargarTodas = async () => {
    if (!confirm('Se descargará un ZIP con TODAS las notas pendientes de envío. Esto puede tardar varios minutos. ¿Continuar?')) return;
    setDescargandoTodas(true);
    try {
      const res = await api.post('/notas-afiliados/zip', { todos: true }, { responseType: 'blob', timeout: 600000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'notas_todas.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('ZIP descargado');
      loadResumen();
      loadNotas();
    } catch {
      toast.error('Error al generar el ZIP');
    } finally {
      setDescargandoTodas(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notas a Afiliados Duplicados</h1>
          <p className="text-sm text-gray-500 mt-1">Art. 16° — Res. 002/2026 I-SEP</p>
        </div>
        {puedeEscribir && (
          <button
            onClick={handleGenerar}
            disabled={generando}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generando ? 'Generando...' : 'Generar notas pendientes'}
          </button>
        )}
      </div>

      {loadingResumen ? (
        <div className="text-center py-4 text-gray-500 text-sm">Cargando resumen...</div>
      ) : resumen ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white border rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{resumen.totalDuplicados}</div>
            <div className="text-xs text-gray-500 mt-1">Afiliados duplicados</div>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{resumen.partidosInvolucrados}</div>
            <div className="text-xs text-gray-500 mt-1">Partidos involucrados</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-amber-700">{resumen.pendientes}</div>
            <div className="text-xs text-amber-600 mt-1">Sin descargar</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{resumen.generadas + resumen.descargadas}</div>
            <div className="text-xs text-blue-600 mt-1">Con PDF</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-700">{resumen.enviadas}</div>
            <div className="text-xs text-gray-500 mt-1">Enviadas</div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Filtrar por estado:</label>
          <select
            value={estadoFiltro}
            onChange={e => { setEstadoFiltro(e.target.value); setCurrentPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            {(Object.keys(ESTADO_CONFIG) as EstadoNota[]).map(e => (
              <option key={e} value={e}>{ESTADO_CONFIG[e].label}</option>
            ))}
          </select>
          {total > 0 && (
            <span className="text-sm text-gray-500">
              {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, total)} de {total} notas
            </span>
          )}
        </div>
        <button
          onClick={handleDescargarTodas}
          disabled={descargandoTodas || notas.length === 0}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {descargandoTodas ? 'Generando ZIP...' : `Descargar ZIP (${notas.filter(n => n.estado !== 'ENVIADA').length})`}
        </button>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {loadingNotas ? (
          <div className="text-center py-10 text-gray-500 text-sm">Cargando notas...</div>
        ) : notas.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">
            {estadoFiltro
              ? 'No hay notas con ese estado.'
              : 'No hay notas generadas. Usá el botón "Generar notas pendientes".'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Apellido y nombre</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">DNI</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Partidos</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Generada</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Descargada</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Enviada</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notas.map(nota => {
                const cfg = ESTADO_CONFIG[nota.estado];
                return (
                  <tr key={nota.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{nota.apellido}, {nota.nombre}</td>
                    <td className="px-4 py-3 text-gray-700 tabular-nums">{formatDNI(nota.dni)}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                    <span>{nota.partidos.map(p => typeof p === 'string' ? p : p.denominacion).join(', ')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 tabular-nums">{formatFecha(nota.fechaGeneracion)}</td>
                    <td className="px-4 py-3 text-gray-500 tabular-nums">{formatFecha(nota.fechaDescarga)}</td>
                    <td className="px-4 py-3 text-gray-500 tabular-nums">{formatFecha(nota.fechaEnvio)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => descargarPdf(nota)}
                          disabled={descargandoId === nota.id}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50"
                        >
                          {descargandoId === nota.id ? '...' : 'PDF'}
                        </button>
                        {puedeEscribir && nota.estado !== 'ENVIADA' && (
                          <button
                            onClick={() => handleMarcarEnviada(nota)}
                            disabled={marcandoId === nota.id}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium disabled:opacity-50"
                          >
                            {marcandoId === nota.id ? '...' : 'Enviada'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
